import { Application, Rectangle, TextureSource } from 'pixi.js';
import { vertexX, vertexY } from './render/iso';
import { AudioLayer } from './audio/AudioLayer';
import { getEngine } from './audio/engine';
import { GAME_TRACK, TITLE_TRACK } from './audio/music';
import { mountSoundboard } from './audio/soundboard';
import { AttentionBus } from './bridge/AttentionBus';
import { CommandBus } from './bridge/CommandBus';
import { FpsMeter } from './debug/fpsMeter';
import { InputController } from './input/InputController';
import { GrayboxTextures } from './render/graybox';
import { GameRenderer } from './render/Renderer';
import { BUILDING_DEFS } from './sim/constants';
import { clearSave, disablePersistence, hasSave, loadGame, saveGame } from './sim/save';
import { Simulation } from './sim/sim';
import { Hud } from './ui/hud';
import { IconFactory } from './ui/icons';
import { loadSettings } from './ui/settings';
import { showTitleScreen } from './ui/TitleScreen';
import { Tutorial } from './ui/Tutorial';
import { IS_WEB } from './variant';
import { startLoop } from './loop';

const TUTORIAL_SEED = 1337; // the test-suite seed: iron guaranteed east of the stake

async function boot(): Promise<void> {
  TextureSource.defaultOptions.scaleMode = 'nearest';
  const app = new Application();
  await app.init({
    resizeTo: window,
    antialias: false,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
    roundPixels: true,
    background: 0x1a1410,
    preference: 'webgl',
  });
  document.querySelector('#app')!.append(app.canvas);

  const params = new URLSearchParams(location.search);
  const tutorialMode = params.has('tutorial');
  const seedParam = params.get('seed');
  const seed = seedParam ? Number(seedParam) >>> 0 : tutorialMode ? TUTORIAL_SEED : Date.now() >>> 0;
  // ?qa: throwaway session that never reads or writes the player's save,
  // including the persistent territorial-best record.
  const qaMode = params.has('qa');
  if (qaMode) disablePersistence();
  // The web build never touches game saves (but keeps settings and the
  // territorial best — those aren't saves).
  const canPersist = !qaMode && !IS_WEB;
  const sim = (params.has('new') || !canPersist ? null : loadGame()) ?? Simulation.newGame(seed);

  const bus = new CommandBus(sim);
  const attention = new AttentionBus();

  // Sound: everything synthesized at runtime. The engine wakes on the first
  // user gesture (browser autoplay policy) — the title click, a soundboard
  // button, or any tap into the game itself.
  const audioEngine = getEngine();
  audioEngine.applySettings(loadSettings());
  // The soundboard is a listening room: its own buttons are the only sound
  // source there, so the game's audio layer (auto guitar + wind) stays out.
  const audio = params.has('gallery') || params.has('soundboard') ? null : new AudioLayer(audioEngine);
  if (audio) bus.onDispatch = (command) => audio.onCommand(command);
  const unlock = (): void => audioEngine.ensureUnlocked();
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);

  const tex = new GrayboxTextures(app.renderer);
  const renderer = new GameRenderer(app, tex, sim.state, attention);
  const input = new InputController(app, sim, bus);
  const uiRoot = document.querySelector<HTMLElement>('#ui-root')!;
  const icons = new IconFactory(app.renderer, tex);

  // Photograph the town for the Gazette: extract a frame around the buildings'
  // centroid with the camera transform temporarily removed.
  const snapshot = (): HTMLCanvasElement | null => {
    try {
      const buildings = sim.state.buildings;
      if (buildings.length === 0) return null;
      let cx = 0;
      let cy = 0;
      for (const building of buildings) {
        cx += vertexX(building.x, building.y);
        cy += vertexY(building.x, building.y);
      }
      cx /= buildings.length;
      cy /= buildings.length;
      const world = renderer.world;
      const prevScale = world.scale.x;
      const prevX = world.position.x;
      const prevY = world.position.y;
      world.scale.set(1);
      world.position.set(0, 0);
      const canvas = app.renderer.extract.canvas({
        target: world,
        frame: new Rectangle(cx - 380, cy - 260, 760, 480),
      }) as HTMLCanvasElement;
      world.scale.set(prevScale);
      world.position.set(prevX, prevY);
      return canvas;
    } catch {
      return null;
    }
  };

  const hud = new Hud(uiRoot, sim, input, bus, icons, app.canvas, snapshot);

  // ?gallery: art-review grid of every building texture at 2x.
  if (params.has('gallery')) {
    const overlay = document.createElement('div');
    overlay.className = 'gallery';
    for (const type of Object.keys(BUILDING_DEFS) as (keyof typeof BUILDING_DEFS)[]) {
      const canvas = icons.buildingIcon(type);
      canvas.style.width = `${canvas.width * 2}px`;
      canvas.style.height = `${canvas.height * 2}px`;
      canvas.style.maxWidth = 'none';
      canvas.style.maxHeight = 'none';
      const cell = document.createElement('div');
      cell.className = 'gallery-cell';
      const label = document.createElement('div');
      label.textContent = type;
      cell.append(canvas, label);
      overlay.append(cell);
    }
    uiRoot.append(overlay);
  }
  const fps = new FpsMeter(uiRoot, params.has('debug'));

  // Autosave: weekly boundary, 45s throttle, and when the tab goes away.
  // Deferred until the player is actually IN a game (title dismissed, or a
  // ?new=1 direct restart) so an idle title screen can't write a phantom
  // week-0 save, and tutorial sessions never overwrite a real town.
  let autosaveStarted = false;
  const startAutosave = (): void => {
    if (autosaveStarted || !canPersist) return;
    autosaveStarted = true;
    let lastSavedWeek = sim.week;
    let lastAutoSaveMs = performance.now();
    app.ticker.add(() => {
      const now = performance.now();
      if (sim.week !== lastSavedWeek) {
        lastSavedWeek = sim.week;
        lastAutoSaveMs = now;
        saveGame(sim);
      } else if (now - lastAutoSaveMs > 45_000) {
        lastAutoSaveMs = now;
        saveGame(sim);
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) saveGame(sim);
    });
    window.addEventListener('pagehide', () => saveGame(sim));
  };

  // Tutorial: the scripted first season. Never autosaves; the exit modal
  // decides whether this claim replaces the player's town.
  let tutorial: Tutorial | null = null;
  if (tutorialMode && !params.has('gallery')) {
    tutorial = new Tutorial({
      root: uiRoot,
      view: sim,
      bus,
      input,
      hud,
      attention,
      isWeb: IS_WEB,
      hadSave: !IS_WEB && hasSave(),
      onAdopt: startAutosave,
    });
    hud.suppressAutoOpenLetters = true;
  }

  // Title screen: everywhere except QA/gallery sessions, tutorial boots, and
  // explicit ?new=1 restarts (those drop straight into the fresh game).
  const showTitle = !qaMode && !params.has('gallery') && !tutorialMode && !params.has('new');
  if (showTitle) {
    const savedSpeed = sim.state.meta.speed;
    sim.state.meta.speed = 0; // pre-loop, race-free
    if (audio) audio.initialTrack = TITLE_TRACK; // chiptune while the menu is up
    const enterGameMusic = (): void => {
      if (audio) {
        audio.initialTrack = GAME_TRACK;
        void audio.music.play(GAME_TRACK);
      }
    };
    const resumeSpeed = (): void => {
      enterGameMusic();
      bus.dispatch({ kind: 'setSpeed', speed: savedSpeed === 0 ? 1 : savedSpeed });
    };
    showTitleScreen(uiRoot, {
      isWeb: IS_WEB,
      canResume: canPersist && hasSave(),
      onResume: () => {
        resumeSpeed();
        startAutosave();
      },
      onNewGame: () => {
        if (canPersist && hasSave()) {
          // A saved town exists and the player confirmed abandoning it.
          clearAndRestart();
          return;
        }
        resumeSpeed();
        startAutosave();
      },
      onTutorial: () => {
        location.search = `tutorial=1&new=1&seed=${TUTORIAL_SEED}`;
      },
    });
  } else if (params.has('new') && !tutorialMode) {
    startAutosave();
  }

  // ?soundboard: audio-review page, the ?gallery of sound.
  if (params.has('soundboard')) {
    mountSoundboard(uiRoot, audioEngine);
  }

  // Opportunistic autoplay — AFTER the title branch has chosen the right
  // theme. When the browser allows it (trusted returning player, embeds
  // with autoplay delegation), music starts on load; otherwise the graph
  // sits armed and silent until the first click/keypress resumes it.
  if (!params.has('soundboard')) audioEngine.tryAutoplay();

  if (params.has('debug') || params.has('soundboard')) {
    // Dev/QA hook: drive the sim synchronously regardless of rAF throttling.
    (window as unknown as Record<string, unknown>).__boom = { sim, bus, app, input, renderer, tutorial, audio };
    (window as unknown as Record<string, unknown>).__boomAudio = audioEngine;
  }

  startLoop(app, sim, renderer, input, hud, fps, tutorial, audio);

  // A refresh after ?new/?tutorial must do a normal boot (load save, show
  // title), not silently restart — strip the one-shot params.
  if (params.has('new') || params.has('tutorial')) {
    const kept = new URLSearchParams(location.search);
    kept.delete('new');
    kept.delete('tutorial');
    kept.delete('seed');
    history.replaceState(null, '', location.pathname + (kept.size > 0 ? `?${kept}` : ''));
  }
}

function clearAndRestart(): void {
  clearSave();
  const params = new URLSearchParams(location.search);
  params.set('new', '1');
  params.set('seed', String(Date.now() >>> 0));
  location.search = params.toString();
}

void boot();
